import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSiteBranding from '../hooks/useSiteBranding';
import './CheckoutSuccess.css';

const CheckoutSuccess = ({ isVisible, onClose, orderDetails = {} }) => {
  const { siteName, siteLogoUrl } = useSiteBranding();
  const [isShowing, setIsShowing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isVisible) {
      setIsShowing(true);
      // Auto redirect to MyOrders after 6 seconds
      const timer = setTimeout(() => {
        navigate('/orders');
        handleClose();
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, navigate]);

  const handleClose = () => {
    setIsShowing(false);
    setTimeout(onClose, 500); // Wait for animation to complete
  };

  const handleTrackOrder = () => {
    navigate('/orders');
    handleClose();
  };

  if (!isVisible) return null;

  const {
    orderNumber = 'SW' + Date.now().toString().slice(-6),
    total = 0,
    items = [],
    deliveryDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    paymentMethod = 'Credit Card',
    paymentStatus = 'Paid'
  } = orderDetails;

  return (
    <div className={`checkout-success-overlay ${isShowing ? 'show' : ''}`} onClick={handleClose}>
      <div className="checkout-success-modal" onClick={(e) => e.stopPropagation()}>
        {/* Success Animation */}
        <div className="success-animation">
          <div className="checkmark-circle">
            <div className="checkmark"></div>
          </div>
          <div className="celebration-particles">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className={`particle particle-${i + 1}`}></div>
            ))}
          </div>
        </div>

        {/* Success Content */}
        <div className="success-content">
          {siteLogoUrl && (
            <div className="checkout-success-brand">
              <img src={siteLogoUrl} alt={siteName} className="checkout-success-logo" />
            </div>
          )}
          <h1 className="success-title">
            <i className="fas fa-party-horn"></i>
            Order Placed Successfully!
          </h1>
          <p className="success-subtitle">
            Thank you for choosing Super Wear. Your order has been confirmed!
          </p>

          {/* Order Summary Card */}
          <div className="order-summary-card">
            <div className="order-header">
              <div className="order-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="order-info">
                <h3>Order #{orderNumber}</h3>
                <p>Expected Delivery: {deliveryDate}</p>
                <p className="payment-info">
                  <i className="fas fa-credit-card"></i>
                  {paymentMethod} • {paymentStatus}
                </p>
              </div>
              <div className="order-total">
                <span className="total-label">Total Paid</span>
                <span className="total-amount">Rs. {total.toLocaleString()}</span>
              </div>
            </div>

            {/* Order Items Preview */}
            {items.length > 0 && (
              <div className="order-items-preview">
                <h4>Items Ordered ({items.length})</h4>
                <div className="items-list">
                  {items.slice(0, 3).map((item, index) => (
                    <div key={index} className="item-preview">
                      <img src={item.image || '/api/placeholder/60/60'} alt={item.name} />
                      <div className="item-details">
                        <span className="item-name">{item.name}</span>
                        <span className="item-specs">
                          {item.size && `Size: ${item.size}`}
                          {item.color && ` • Color: ${item.color}`}
                          {` • Qty: ${item.quantity}`}
                        </span>
                      </div>
                      <span className="item-price">Rs. {(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <div className="more-items">
                      +{items.length - 3} more items
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="success-actions">
            <button className="btn-primary track-order" onClick={handleTrackOrder}>
              <i className="fas fa-truck"></i>
              Track Your Order
            </button>
            <button className="btn-secondary continue-shopping" onClick={handleClose}>
              <i className="fas fa-shopping-bag"></i>
              Continue Shopping
            </button>
          </div>

          {/* Additional Info */}
          <div className="additional-info">
            <div className="info-item">
              <i className="fas fa-envelope"></i>
              <span>Order confirmation sent to your email</span>
            </div>
            <div className="info-item">
              <i className="fas fa-headset"></i>
              <span>Need help? Contact our support team</span>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <button className="close-modal-btn" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
    </div>
  );
};

// Global function to show checkout success
export const showCheckoutSuccess = (orderDetails) => {
  const event = new CustomEvent('showCheckoutSuccess', { detail: orderDetails });
  window.dispatchEvent(event);
};

export default CheckoutSuccess; 